import { env } from "../env";

const CDN_URL = env.NEXT_PUBLIC_MEDIA_DISTRIBUTION_URL;

export type MediaStatus =
  | "UPLOADED"
  | "PROCESSING"
  | "READY"
  | "FAILED"
  | "REJECTED";

export interface MediaDerivativeFile {
  Key: string;
  MimeType: string;
}

type ImageVariant = {
  jpg?: MediaDerivativeFile;
  webp?: MediaDerivativeFile;
};

type ImageDerivatives = {
  sm?: ImageVariant;
  md?: ImageVariant;
  lg?: ImageVariant;
};

type VideoDerivatives = {
  original?: {
    mp4?: MediaDerivativeFile;
  };
};

type ThumbnailDerivatives = {
  default?: {
    jpg?: MediaDerivativeFile;
  };
};

type DocumentDerivatives = {
  original?: Record<string, MediaDerivativeFile>;
};

export type MediaDerivatives = {
  image?: ImageDerivatives;
  video?: VideoDerivatives;
  thumbnail?: ThumbnailDerivatives;
  document?: DocumentDerivatives;
};

export type MediaCategory = "image" | "video" | "document";

export interface MediaItem {
  MediaId: string;
  Status: MediaStatus;
  OriginalFileName: string;
  MediaCategory: MediaCategory;
  FileExtension: string;
  Derivatives?: MediaDerivatives;
  localUrl?: string;
  room_tag?: string;
  room_order?: number;
}

type ResolvedMedia = {
  type: MediaCategory;

  src: string; // main URL (always exists if not null)
  preview?: string; // thumbnail / poster
  webp?: string; // only for images

  name?: string; // for documents
  ext?: string; // file extension
  room_tag?: string;
  room_order?: number;
  MediaId?: string;
};

type ImageSize = "sm" | "md" | "lg";

export function getMedia(
  media: MediaItem,
  size: ImageSize = "md",
): ResolvedMedia | null {
  if (!CDN_URL) return null;
  const CDN = CDN_URL;
  if (!media) return null;
  // 🚀 DRAFT FALLBACK: If not READY, return the raw S3 source URL
  if (media.Status === "UPLOADED" || media.Status === "PROCESSING") {
    const rawKey = `drafts/${media.MediaId}/source.${media.FileExtension}`;
    const src = `${CDN}/${rawKey}`;

    return {
      type: media.MediaCategory,
      src: src,
      // For drafts, we don't have these yet
      preview: undefined,
      webp: undefined,
      name: media.OriginalFileName,
      ext: media.FileExtension,
      room_tag: media.room_tag,
      room_order: media.room_order,
      MediaId: media.MediaId,
    };
  }

  if (media.Status !== "READY") return null;
  // ---------------- IMAGE ----------------
  if (media.MediaCategory === "image") {
    const img = media.Derivatives?.image;
    if (!img) return null;

    let variant;

    if (size === "sm") {
      variant = img.sm || img.md || img.lg;
    } else if (size === "md") {
      variant = img.md || img.lg;
    } else {
      variant = img.lg;
    }

    if (!variant) return null;

    const jpgKey = variant.jpg?.Key || img.lg?.jpg?.Key;

    if (!jpgKey) return null;

    const webpKey = variant.webp?.Key;

    return {
      type: "image",
      src: `${CDN}/${jpgKey}`,
      webp: webpKey ? `${CDN}/${webpKey}` : undefined,
      room_tag: media.room_tag,
      room_order: media.room_order,
      MediaId: media.MediaId,
    };
  }

  // ---------------- VIDEO ----------------
  if (media.MediaCategory === "video") {
    const videoKey = media.Derivatives?.video?.original?.mp4?.Key;

    if (!videoKey) return null;

    const thumbKey = media.Derivatives?.thumbnail?.default?.jpg?.Key;

    return {
      type: "video",
      src: `${CDN}/${videoKey}`,
      preview: thumbKey ? `${CDN}/${thumbKey}` : undefined,
    };
  }

  // ---------------- DOCUMENT ----------------
  if (media.MediaCategory === "document") {
    const ext = media.FileExtension?.toLowerCase();

    const key = media.Derivatives?.document?.original?.[ext]?.Key;

    if (!key) return null;

    return {
      type: "document",
      src: `${CDN}/${key}`,
      name: media.OriginalFileName,
      ext,
    };
  }

  return null;
}
